import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { Sidebar } from "./sidebar";
import { Topbar } from "./topbar";
import { AiBar } from "@/components/ai-bar/ai-bar";
import { AskPanel, type AskResult } from "@/components/ai-bar/ask-panel";
import { AdvisePanel, type AdviseResult } from "@/components/ai-bar/advise-panel";
import { ProactiveSuggestion } from "@/components/ai-bar/proactive-suggestion";
import { useAiBar } from "@/hooks/use-ai-bar";
import { PageTransition } from "@/components/transitions/page-transition";
import { ShortcutOverlay } from "@/components/shortcuts/shortcut-overlay";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import TrialBanner from "@/components/billing/trial-banner";
import { MobileBottomNav } from "@/components/mobile-nav";
import { normalizeFeatureUsageKey, trackFeatureUsage } from "@/hooks/use-intelligent-density";
import { FocusModeProvider } from "@/hooks/use-focus-mode";
import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import { NpsModal } from "@/components/nps-csat-modal";
import CookieConsent from "@/components/cookie-consent";

const NPS_DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

interface NpsPromptSummary {
  shouldPrompt: boolean;
}

/**
 * Main dashboard layout. Wraps all authenticated routes.
 * Sidebar (left, fixed 256px) + Topbar (top, 56px) + Content (fluid).
 * AI bar fixed at bottom (C1). Density provider wraps content (C5-C7).
 * Responsive: sidebar collapses on mobile (Council Condition 4).
 */
export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showNpsPrompt, setShowNpsPrompt] = useState(false);
  const location = useLocation();
  const { session, isLoading: isAuthLoading } = useAuth();
  const isBuilderRoute = location.pathname.startsWith("/pages");
  const aiBar = useAiBar(!isBuilderRoute);

  useEffect(() => {
    const featureKey = normalizeFeatureUsageKey(location.pathname);
    if (!featureKey) {
      return;
    }
    trackFeatureUsage(featureKey);
  }, [location.pathname]);

  // C29: Keyboard shortcuts (G+key nav, N+key create, ? overlay)
  useKeyboardShortcuts(() => setShowShortcuts(true));

  useEffect(() => {
    if (isAuthLoading || !session?.activeOrganizationId) {
      return;
    }

    const dismissKey = `feedback:nps:dismissed:${session.activeOrganizationId}`;
    const dismissedAtRaw = window.localStorage.getItem(dismissKey);
    if (dismissedAtRaw) {
      const dismissedAt = Number(dismissedAtRaw);
      if (Number.isFinite(dismissedAt) && Date.now() - dismissedAt < NPS_DISMISS_TTL_MS) {
        return;
      }
      window.localStorage.removeItem(dismissKey);
    }

    let cancelled = false;

    void apiFetch<{ data: NpsPromptSummary }>("/v1/platform/feedback/nps")
      .then((response) => {
        if (!cancelled && response.data.shouldPrompt) {
          setShowNpsPrompt(true);
        }
      })
      .catch(() => {
        // Non-blocking prompt path.
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthLoading, session?.activeOrganizationId]);

  const handleNpsClose = () => {
    if (session?.activeOrganizationId) {
      window.localStorage.setItem(
        `feedback:nps:dismissed:${session.activeOrganizationId}`,
        String(Date.now()),
      );
    }
    setShowNpsPrompt(false);
  };

  // Resolve panel content based on mode + lastResult
  const resolvedPanelContent = useMemo(() => {
    // If explicit panel content is set, use it
    if (aiBar.panelContent) return aiBar.panelContent;

    // Proactive suggestion (pulse clicked, no query yet)
    if (aiBar.mode === "advise" && aiBar.currentSuggestion && !aiBar.result) {
      return (
        <ProactiveSuggestion
          suggestion={aiBar.currentSuggestion}
          onAccept={() => aiBar.setMode("idle")}
          onDismiss={() => aiBar.setMode("idle")}
        />
      );
    }

    // Ask mode result
    if (aiBar.mode === "ask" && aiBar.answer) {
      const askResult: AskResult = {
        content: aiBar.answer,
      };
      return <AskPanel result={askResult} isLoading={aiBar.isProcessing} error={aiBar.error} />;
    }

    // Advise mode result
    if (aiBar.mode === "advise" && aiBar.advice) {
      const adviseResult: AdviseResult = {
        recommendation: aiBar.advice.recommendation,
        title: aiBar.advice.title,
        reasoning: aiBar.advice.reasoning,
        confidence: aiBar.advice.confidence,
      };
      return <AdvisePanel result={adviseResult} isLoading={aiBar.isProcessing} error={aiBar.error} />;
    }

    if (aiBar.mode === "do" && aiBar.activeTask) {
      const askResult: AskResult = {
        content:
          aiBar.result?.content ??
          "Task prepared for approval. Nothing will execute until you review and approve it.",
        data: [
          { label: "Agent", value: aiBar.activeTask.agentType.replaceAll("_", " ") },
          { label: "State", value: aiBar.activeTask.state.replaceAll("_", " ") },
          { label: "Execution", value: "Auto-execute after approval" },
        ],
      };
      return <AskPanel result={askResult} isLoading={aiBar.isProcessing} error={aiBar.error} />;
    }

    if (aiBar.error) {
      if (aiBar.mode === "advise") {
        return (
          <AdvisePanel
            result={{ recommendation: "maybe", title: "Assistant unavailable", reasoning: "", confidence: 0 }}
            isLoading={false}
            error={aiBar.error}
          />
        );
      }

      return (
        <AskPanel
          result={{ content: "Assistant unavailable." }}
          isLoading={false}
          error={aiBar.error}
        />
      );
    }

    // Processing state (loading skeleton)
    if (aiBar.isProcessing) {
      if (aiBar.mode === "ask") return <AskPanel result={{ content: "" }} isLoading />;
      if (aiBar.mode === "advise") return <AdvisePanel result={{ recommendation: "yes", title: "", reasoning: "", confidence: 0 }} isLoading />;
    }

    return null;
  }, [aiBar.activeTask, aiBar.advice, aiBar.answer, aiBar.currentSuggestion, aiBar.error, aiBar.isProcessing, aiBar.mode, aiBar.panelContent, aiBar.result, aiBar.setMode]);

  return (
    <FocusModeProvider>
      <div className="flex h-screen overflow-hidden bg-background">
        <a
          href="#main-content"
          className="sr-only fixed left-4 top-4 z-[90] rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground focus:not-sr-only focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Skip to main content
        </a>

        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <div className="flex flex-1 flex-col overflow-hidden">
          <Topbar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />

          <main
            id="main-content"
            data-main-content
            className={`flex-1 overflow-y-auto p-6 ${isBuilderRoute ? "pb-20 md:pb-6" : "pb-20 md:pb-6"}`}
          >
            {/* B16: Trial banner — non-intrusive, above content */}
            <TrialBanner />
            <PageTransition>
              <Outlet />
            </PageTransition>

            {/* CL.2: Legal footer links */}
            <footer
              data-non-essential
              className="mt-12 border-t border-[var(--color-border)] pt-4 pb-2 flex items-center justify-center gap-4 text-xs text-[var(--color-muted-foreground)]"
            >
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--color-foreground)]">Privacy</a>
              <span aria-hidden="true">&middot;</span>
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--color-foreground)]">Terms</a>
              <span aria-hidden="true">&middot;</span>
              <a href="/cookies" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--color-foreground)]">Cookies</a>
              <span aria-hidden="true">&middot;</span>
              <a href="/legal/dpa" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--color-foreground)]">DPA</a>
            </footer>
          </main>

          <MobileBottomNav />
        </div>

        {/* C29: Keyboard shortcut overlay */}
        <ShortcutOverlay isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />

        {/* C1: Persistent AI Bar */}
        {isBuilderRoute ? null : (
          <AiBar
            isPulsing={aiBar.isPulsing}
            onSubmit={aiBar.handleSubmit}
            onPulseClick={aiBar.handlePulseClick}
            mode={aiBar.mode}
            isProcessing={aiBar.isProcessing}
            isDisabled={aiBar.isRuntimeUnavailable}
            disabledPlaceholder={
              aiBar.runtimeUnavailableReason ??
              "AI assistant unavailable. Refresh the workspace and try again."
            }
          >
            {resolvedPanelContent}
          </AiBar>
        )}

        <NpsModal
          isOpen={showNpsPrompt}
          onClose={handleNpsClose}
          context="day_14_workspace_prompt"
        />
        <CookieConsent />
      </div>
    </FocusModeProvider>
  );
}
